import BackButton from '@/components/back-button/back-button';
import NavigationBar, { NavigationBarProps } from '@/components/layout/navigation-bar/navigation-bar';
import useLoadingController, { LoadingState } from '@/components/loading/useLoading.controller';
import customTheme from '@/constants/theme.custom';
import useLoadingScreenShown from '@/hooks/useLoadingScreenShown';
import { Box } from '@cmpsr/components';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomProgressBar } from '../ppra-loading-page/components';
import ArtWaveSvg from '../ppra-loading-page/components/ArtWaveSvg';

export interface BaseLoadingScreenProps {
  variant?: 'light' | 'dark';
  navBarVariant?: NavigationBarProps['variant'];
  backButtonVariant?: 'light' | 'dark';
  componentsAboveProgressBar?: ReactNode[];
  componentsBelowProgressBar?: ReactNode[];
  minimumLoadingSeconds?: number;
  progressHaltAt?: number;
  externalLoadingState?: LoadingState;
  onLoadingComplete: () => void;
  to?: string;
  onBack?: () => void;
  screenId?: string; // Unique identifier for this loading screen
  stickyButton?: ReactNode; // New prop for sticky button
}

/**
 * A base loading screen component that can be reused by different PPRA loading screens.
 * Shows loading animation and calls onLoadingComplete when finished.
 */
const BaseLoadingScreen: React.FC<BaseLoadingScreenProps> = ({
  variant = 'dark',
  navBarVariant = 'light',
  backButtonVariant = 'dark',
  componentsAboveProgressBar = [],
  componentsBelowProgressBar = [],
  minimumLoadingSeconds = 3,
  progressHaltAt = 90,
  externalLoadingState,
  onLoadingComplete,
  to,
  onBack,
  screenId = 'default', // Default ID if none provided
  stickyButton,
}) => {
  const navigate = useNavigate();
  // Check if this loading screen has been shown before
  const { isFirstTime, markAsSeen } = useLoadingScreenShown(screenId);

  // If minimumLoadingSeconds is provided and this isn't the first time,
  // reduce the loading time to make it faster on repeat viewings
  const effectiveMinimumSeconds = !isFirstTime ? Math.min(1, minimumLoadingSeconds) : minimumLoadingSeconds;

  const { progress, loadingState } = useLoadingController({
    minimumSeconds: effectiveMinimumSeconds,
    progressHaltAt: progressHaltAt,
    externalLoadingState,
  });

  const [maxWaveHeight, setMaxWaveHeight] = useState('auto');
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate the max height for the wave based on viewport height
  useEffect(() => {
    const calculateMaxHeight = () => {
      if (containerRef.current) {
        const viewportHeight = window.innerHeight;
        const maxAllowedHeight = viewportHeight * 0.2; // 20% of viewport height
        setMaxWaveHeight(`${maxAllowedHeight}px`);
      }
    };

    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);

    return () => window.removeEventListener('resize', calculateMaxHeight);
  }, []);

  useEffect(() => {
    // If it's not the first time, complete loading quickly
    if (!isFirstTime) {
      const timer = setTimeout(() => {
        onLoadingComplete();
      }, 100);
      return () => clearTimeout(timer);
    }
    // Otherwise, wait for loading state to complete
    else if (loadingState === LoadingState.success) {
      markAsSeen();
      onLoadingComplete();
    }
  }, [loadingState, isFirstTime, onLoadingComplete, markAsSeen]);

  // Create the appropriate back button based on props
  const renderBackButton = () => {
    if (onBack) {
      // The back button will immediately trigger the provided callback
      // which should skip the loading process and navigate away
      return <BackButton variant={backButtonVariant} onClick={onBack} />;
    }

    // If no onBack provided, default to navigate(-1)
    const handleDefaultBack = () => {
      navigate(-1);
    };

    // Use navigate(-1) as default back behavior, fallback to to prop if needed
    return <BackButton variant={backButtonVariant} onClick={handleDefaultBack} />;
  };

  // Otherwise, show the loading screen
  return (
    <Box
      width="100%"
      height="100vh"
      background={variant === 'light' ? customTheme.colors.tan : customTheme.colors.gradient.dark}
      position="fixed"
      top="0"
      left="0"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      ref={containerRef}
    >
      {/* Main content container */}
      <Box flex="1" display="flex" flexDirection="column" overflow="hidden" position="relative" zIndex="1">
        {/* Sticky top section */}
        <Box
          position="sticky"
          top="0"
          zIndex="3"
          background={variant === 'light' ? customTheme.colors.tan : customTheme.colors.gradient.dark}
        >
          <NavigationBar variant={navBarVariant} leftContent={renderBackButton()} />

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width={{ base: '315px', lg: '600px' }}
            mx="auto"
            gap="spacer-8"
            pt={{ base: 'spacer-8', lg: 'spacer-16' }}
            pb="spacer-8"
          >
            {/* Render components above the progress bar */}
            {componentsAboveProgressBar.map((component, index) => (
              <React.Fragment key={`above-${index}`}>{component}</React.Fragment>
            ))}

            {/* Progress bar */}
            <CustomProgressBar
              progress={progress}
              progressBarBackgroundColor={variant === 'light' ? 'med-blue-25' : 'med-blue'}
            />
          </Box>
        </Box>

        {/* Scrollable bottom section */}
        <Box
          flex="1"
          overflowY="auto"
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          minHeight="250px"
        >
          {/* Render components below the progress bar */}
          {componentsBelowProgressBar.map((component, index) => (
            <Box width="100%" key={`below-${index}`} overflow="hidden" pointerEvents={index === 0 ? 'none' : 'auto'}>
              {component}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Footer at the bottom */}
      <Box zIndex="0" position="relative" pt="spacer-8">
        {stickyButton && (
          <Box display="flex" justifyContent="center" zIndex="4" mb="spacer-40">
            {stickyButton}
          </Box>
        )}
        <Box
          zIndex="-1"
          overflow="hidden"
          maxHeight={maxWaveHeight}
          position="absolute"
          bottom="0"
          left="0"
          width="100%"
        >
          <ArtWaveSvg />
        </Box>
      </Box>
    </Box>
  );
};

export default BaseLoadingScreen;
